import { disposeObject3D } from "./three-dispose.js";
import { createTextSprite } from "./text-sprite.js";
import { VIS_COLORS } from "./vis-colors.js";
import * as THREE from "three";
//#region ../src/visualization/gps-compass-cubes.ts
/**
* GPS Compass Cubes Module
*
* Creates colored cubes at ±1 m along each axis with text labels
* (N, E, S, W) to visualize GPS/NUE orientation in the 3D scene.
*
* NUE convention: X = North, Y = Up, Z = East.
*
* Designed to be added as children of a CameraFollower so they
* stay near the camera but always GPS-world-aligned.
*
* @see gps-plus-slam/GpsPlusSlamJs_Docs/docs/2026-03-12-user-feedback.md Issue 8
*/
/** Side length of each compass cube in metres. */
const COMPASS_CUBE_SIZE = .1;
/** Distance from origin to each compass cube in metres. */
const COMPASS_CUBE_DISTANCE = 1;
const CUBE_SPECS = [
	{
		name: "compass-north",
		color: VIS_COLORS.COMPASS_NORTH.hex,
		position: [
			1,
			0,
			0
		],
		label: "N"
	},
	{
		name: "compass-east",
		color: VIS_COLORS.COMPASS_EAST.hex,
		position: [
			0,
			0,
			1
		],
		label: "E"
	},
	{
		name: "compass-south",
		color: VIS_COLORS.COMPASS_SOUTH.hex,
		position: [
			-1,
			0,
			0
		],
		label: "S"
	},
	{
		name: "compass-west",
		color: VIS_COLORS.COMPASS_WEST.hex,
		position: [
			0,
			0,
			-1
		],
		label: "W"
	},
	{
		name: "compass-up",
		color: VIS_COLORS.COMPASS_UP.hex,
		position: [
			0,
			1,
			0
		]
	}
];
/**
* Create a static glyph label above a cube via the shared text-sprite helper
* (64×64 canvas defaults: centered white text on transparent background).
*/
function createCubeLabel(text) {
	const { sprite } = createTextSprite({ text });
	sprite.name = `label-${text}`;
	sprite.position.set(0, COMPASS_CUBE_SIZE * 1.5, 0);
	sprite.scale.set(.4, .4, .4);
	return sprite;
}
/**
* Create compass cubes and attach them to a parent Object3D.
*
* @param parent The Object3D to anchor the compass group to
*               (typically the CameraFollower).
*/
function createGpsCompassCubes(parent) {
	const group = new THREE.Group();
	group.name = "gps-compass-cubes";
	const geometry = new THREE.BoxGeometry(COMPASS_CUBE_SIZE, COMPASS_CUBE_SIZE, COMPASS_CUBE_SIZE);
	for (const spec of CUBE_SPECS) {
		const material = new THREE.MeshBasicMaterial({ color: spec.color });
		const mesh = new THREE.Mesh(geometry, material);
		mesh.name = spec.name;
		mesh.position.set(...spec.position);
		if (spec.label) mesh.add(createCubeLabel(spec.label));
		group.add(mesh);
	}
	parent.add(group);
	return {
		group,
		dispose() {
			if (group.parent) group.parent.remove(group);
			disposeObject3D(group);
		}
	};
}
//#endregion
export { COMPASS_CUBE_DISTANCE, COMPASS_CUBE_SIZE, createGpsCompassCubes };
